import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, HeartPulse } from "lucide-react";
import { PETITION_GOAL } from "../lib/constants";
import { usePetition } from "../lib/petitionContext";
import Reveal from "./Reveal";

/**
 * PetitionProgress
 *
 * Prominent homepage section that shows live progress toward the petition goal.
 * The signature count is provided by PetitionProvider (which fetches from
 * our Netlify function on production or a CORS proxy in dev preview).
 *
 * Clicking "Sign the Petition" opens the shared in-site PetitionModal
 * rather than a new browser tab.
 */
const PetitionProgress = () => {
    const { count, openPetition } = usePetition();
    const goal = PETITION_GOAL;
    const btnRef = useRef(null);

    const [animatedCount, setAnimatedCount] = useState(0);

    // Smooth count-up animation whenever the real number changes.
    useEffect(() => {
        if (count === null || count === undefined) return;
        const duration = 1400;
        const start = performance.now();
        const from = animatedCount;
        const to = count;
        if (from === to) return;
        let raf;
        const step = (t) => {
            const p = Math.min(1, (t - start) / duration);
            const eased = 1 - Math.pow(1 - p, 3);
            setAnimatedCount(Math.round(from + (to - from) * eased));
            if (p < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [count]);

    const isLoading = count === null || count === undefined;
    const displayCount = isLoading ? 0 : animatedCount;
    const percent = Math.max(0, Math.min(100, (displayCount / goal) * 100));

    const handleSign = (e) => {
        e.preventDefault();
        openPetition(btnRef);
    };

    return (
        <section
            id="petition-progress"
            data-testid="petition-progress"
            className="relative bg-editorial-muted py-20 md:py-28 overflow-hidden"
            aria-labelledby="petition-progress-heading"
        >
            <div className="absolute inset-0 bg-noise opacity-20 pointer-events-none" />
            <div className="max-w-[1200px] mx-auto px-6 lg:px-10 relative">
                <Reveal className="text-center max-w-3xl mx-auto mb-10 md:mb-12">
                    <p className="overline mb-4 text-coral flex items-center justify-center gap-2">
                        <HeartPulse className="w-4 h-4" />
                        Petition Progress
                    </p>
                    <h2
                        id="petition-progress-heading"
                        className="font-serif text-4xl md:text-5xl lg:text-6xl text-navy-900 leading-[1.05] tracking-tight"
                    >
                        Help us reach <span className="text-coral">10,000 signatures</span>.
                    </h2>
                    <p className="mt-6 text-base md:text-lg text-navy-800/80 leading-relaxed">
                        Every signature helps show the growing demand for AED access, CPR training, and stronger cardiac emergency preparedness in hotels — in support of the <strong>Hotel Cardiac Emergency Preparedness Act</strong>.
                    </p>
                </Reveal>

                <Reveal delay={0.1}>
                    <div className="bg-white border border-editorial-border shadow-lg p-6 md:p-10 lg:p-12 max-w-3xl mx-auto">
                        <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 mb-6">
                            <div>
                                <p className="overline text-navy-700/70 mb-2">Current Signatures</p>
                                <p
                                    className="font-serif text-5xl md:text-6xl lg:text-7xl text-navy-900 leading-none tracking-tight tabular-nums"
                                    data-testid="petition-current-count"
                                    aria-live="polite"
                                    aria-busy={isLoading}
                                >
                                    {isLoading ? "…" : displayCount.toLocaleString("en-US")}
                                </p>
                            </div>
                            <div className="text-left sm:text-right">
                                <p className="overline text-navy-700/70 mb-2">Goal</p>
                                <p className="font-serif text-3xl md:text-4xl text-navy-900/60 leading-none tracking-tight tabular-nums">
                                    {goal.toLocaleString("en-US")}
                                </p>
                            </div>
                        </div>

                        <div
                            className="relative h-4 md:h-5 w-full bg-editorial-muted border border-editorial-border overflow-hidden"
                            role="progressbar"
                            aria-valuenow={displayCount}
                            aria-valuemin={0}
                            aria-valuemax={goal}
                            aria-label={`Petition progress: ${displayCount} of ${goal} signatures`}
                            data-testid="petition-progress-bar"
                        >
                            <motion.div
                                className="absolute inset-y-0 left-0 bg-coral"
                                initial={{ width: 0 }}
                                animate={{ width: `${percent}%` }}
                                transition={{ duration: 1.4, ease: [0.21, 0.47, 0.32, 0.98] }}
                            />
                        </div>
                        <div className="mt-3 flex items-center justify-between text-xs uppercase tracking-[0.18em] text-navy-700/70">
                            <span data-testid="petition-percent">{percent.toFixed(1)}% of goal</span>
                            <span>{Math.max(0, goal - displayCount).toLocaleString("en-US")} to go</span>
                        </div>

                        <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
                            <button
                                ref={btnRef}
                                type="button"
                                onClick={handleSign}
                                data-testid="petition-progress-sign-btn"
                                className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-coral hover:bg-coral-dark text-white font-semibold transition-colors duration-300"
                            >
                                Sign the Petition
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </button>
                            <p className="text-sm text-navy-800/70">
                                Add your name in under a minute — every signature counts.
                            </p>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};

export default PetitionProgress;
